import React, { useState, useEffect } from 'react';
import { Lock, Plus, Clock, Trash2 } from 'lucide-react';
import { ordersApi } from '../../services/api';

const formatTimestamp = (timestamp) => new Date(timestamp).toLocaleString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
});

// Notes internes d'une commande : l'API ne les expose qu'au prothésiste/admin.
// ponytail: pas d'édition (supprimer + réécrire suffit), confirmation via
// window.confirm plutôt qu'une modale dédiée.
const InternalNotes = ({ orderId }) => {
    const [notes, setNotes] = useState([]);
    const [draft, setDraft] = useState(null); // null = formulaire fermé
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        ordersApi.getNotes(orderId)
            .then(data => { if (!cancelled) setNotes(data || []); })
            .catch(() => { if (!cancelled) setError('Impossible de charger les notes.'); });
        return () => { cancelled = true; };
    }, [orderId]);

    const handleSave = async () => {
        const text = (draft || '').trim();
        if (!text) return;
        setIsSaving(true);
        setError(null);
        try {
            const note = await ordersApi.addNote(orderId, text);
            setNotes(prev => [...prev, note]);
            setDraft(null);
        } catch (e) {
            setError('Échec de l\'enregistrement de la note.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (noteId) => {
        if (!window.confirm('Supprimer cette note interne ?')) return;
        setError(null);
        try {
            await ordersApi.deleteNote(orderId, noteId);
            setNotes(prev => prev.filter(n => n.id !== noteId));
        } catch (e) {
            setError('Échec de la suppression de la note.');
        }
    };

    return (
        <div className="bg-black/30 border border-white/10 rounded-lg shadow-sm">
            <div className="px-4 py-3 border-b border-white/10 glass rounded-t-lg flex items-center space-x-2">
                <Lock className="h-4 w-4 text-yellow-400" />
                <h3 className="text-sm font-semibold text-white">Notes internes</h3>
                <span className="text-xs text-gray-400">(non visibles par le client)</span>
            </div>

            <div className="p-4 space-y-3">
                {notes.length === 0 && !draft && (
                    <p className="text-sm text-gray-400">Aucune note interne.</p>
                )}

                {notes.map(note => (
                    <div key={note.id} className="bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2">
                        <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                            <span className="font-medium">{note.author_name}</span>
                            <span className="flex items-center space-x-2">
                                <span className="flex items-center space-x-1">
                                    <Clock className="h-3 w-3" />
                                    <span>{formatTimestamp(note.created_at)}</span>
                                </span>
                                <button
                                    onClick={() => handleDelete(note.id)}
                                    title="Supprimer cette note"
                                    aria-label="Supprimer cette note"
                                    className="text-gray-500 hover:text-red-400 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </span>
                        </div>
                        <p className="text-sm text-gray-200 whitespace-pre-line">{note.note_text}</p>
                    </div>
                ))}

                {error && <p className="text-sm text-red-400">{error}</p>}

                {draft === null ? (
                    <button
                        onClick={() => setDraft('')}
                        className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm text-gray-200 bg-white/[0.07] border border-white/10 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Ajouter une note interne (non visible par le client)</span>
                    </button>
                ) : (
                    <div className="space-y-2">
                        <textarea
                            autoFocus
                            rows={3}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="Note visible uniquement par ShinySmile…"
                            className="w-full px-3 py-2 text-sm bg-black/30 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => { setDraft(null); setError(null); }}
                                className="px-3 py-1.5 text-sm text-gray-300 hover:text-white"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving || !draft.trim()}
                                className="px-3 py-1.5 text-sm bg-yellow-500 text-black font-medium rounded-md hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            >
                                {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InternalNotes;
